import { ApiError } from '../lib/api.js';
import { el, render, fmtDate, fmtDateOnly, fmtRelative, copy } from '../lib/dom.js';
import { avatar, button, renderPagination, skeletonRows, emptyState, errorState, fullRow } from '../lib/ui.js';
import { projectStatusBadge, runStatusBadge, SOURCE_LABEL } from '../lib/status.js';

// ---------------------------------------------------------------------------
// Projects table, shared by the Projects page (every organisation) and the
// org detail Projects tab (one organisation). Each row expands to its
// schedules and every schedule version's run ID.
//
// "Versions" follows the tenant app: an AI-generated schedule's versions are
// its root runs; a contractor submission is a schedule row of its own, so it
// appears as a schedule with no runs. What-if runs are not versions and are
// only counted (see listProjects in the API).
// ---------------------------------------------------------------------------

function copyButton(value, what) {
  return el(
    'button',
    {
      type: 'button',
      class: 'copy-btn',
      title: `Copy this ${what} to the clipboard, for support tickets and logs.`,
      onclick: async (e) => {
        e.stopPropagation();
        const ok = await copy(value);
        e.target.textContent = ok ? 'Copied' : 'Copy failed';
        setTimeout(() => (e.target.textContent = 'Copy'), 1800);
      },
    },
    'Copy',
  );
}

function versionsPanel(project) {
  if (project.schedules.length === 0) {
    return el('div', { class: 'versions-panel' }, el('p', { class: 'versions-empty' }, 'No schedules yet.'));
  }
  return el(
    'div',
    { class: 'versions-panel' },
    project.schedules.map((s) => {
      const whatIfs = s.whatIfRunCount > 0 ? `· ${s.whatIfRunCount} what-if run${s.whatIfRunCount === 1 ? '' : 's'} not listed` : null;
      return el(
        'section',
        { class: 'versions-schedule' },
        el(
          'div',
          { class: 'versions-schedule__head' },
          el('strong', {}, `Schedule v${s.versionNumber}`),
          el('span', { class: 'role-chip' }, SOURCE_LABEL[s.sourceType] ?? s.sourceType),
          el('span', { class: 'role-chip' }, s.status),
          el('span', { class: 'versions-schedule__meta' }, `${s.versions.length} version${s.versions.length === 1 ? '' : 's'}`),
          whatIfs ? el('span', { class: 'versions-schedule__meta' }, whatIfs) : null,
        ),
        s.versions.length === 0
          ? el('p', { class: 'versions-empty' }, 'No runs for this schedule.')
          : el(
              'div',
              { class: 'versions-grid' },
              el('div', { class: 'versions-grid__th' }, 'Version'),
              el('div', { class: 'versions-grid__th' }, 'Run ID'),
              el('div', { class: 'versions-grid__th' }, 'Status'),
              el('div', { class: 'versions-grid__th' }, 'Created'),
              s.versions.flatMap((v) => [
                el('div', {}, v.versionName ?? (v.versionNumber !== null ? `Version ${v.versionNumber}` : '—')),
                el('div', { class: 'versions-grid__run' }, el('span', { class: 'mono', title: v.runId }, v.runId), copyButton(v.runId, 'run ID')),
                el('div', {}, runStatusBadge(v.status)),
                el('div', {}, fmtDate(v.createdAt)),
              ]),
            ),
      );
    }),
  );
}

/**
 * fetchPage(page, pageSize) → { total, projects }. showOrg adds the
 * Organisation column (every-org listing). Returns { root, reload(page?) }.
 */
export function projectsTable({ fetchPage, showOrg = false, navigate, pageSize = 25, emptyText = 'No projects yet.' }) {
  const state = { page: 1, total: 0 };
  const cols = showOrg ? 7 : 6;
  const tbody = el('tbody', {});
  const footer = el('div', {});

  function projectRows(p) {
    const detailRow = el('tr', { class: 'detail-row', hidden: true }, el('td', { colspan: String(cols) }, versionsPanel(p)));
    const toggle = button('Show versions', {
      size: 'sm',
      disabled: p.scheduleCount === 0,
      title: `Show every schedule version of ${p.title} and its run ID. Read-only.`,
      onClick: (e) => {
        e.stopPropagation();
        detailRow.hidden = !detailRow.hidden;
        e.currentTarget.textContent = detailRow.hidden ? 'Show versions' : 'Hide versions';
        e.currentTarget.setAttribute('aria-expanded', String(!detailRow.hidden));
      },
    });
    toggle.setAttribute('aria-expanded', 'false');

    return [
      el(
        'tr',
        {},
        el(
          'td',
          {},
          el(
            'div',
            { class: 'cell-main' },
            el(
              'div',
              { class: 'cell-main__text' },
              el('span', { class: 'cell-title', title: p.title }, p.title),
              el('span', { class: 'cell-sub mono' }, p.id.slice(0, 8), ' ', copyButton(p.id, 'project ID')),
            ),
          ),
        ),
        showOrg
          ? el(
              'td',
              {},
              p.orgName
                ? el(
                    'div',
                    { class: 'cell-badges' },
                    el('button', { type: 'button', class: 'link-btn', title: `Open ${p.orgName}.`, onclick: () => navigate(`/orgs/${p.orgId}?tab=projects`) }, p.orgName),
                    p.orgDeleted ? el('span', { class: 'badge badge--red' }, 'Deleted') : null,
                  )
                : '—',
            )
          : null,
        el(
          'td',
          {},
          p.owner
            ? el(
                'div',
                { class: 'cell-main', title: p.owner.email },
                avatar(p.owner.fullName, { size: 24 }),
                el('span', { class: 'nowrap' }, p.owner.fullName),
              )
            : '—',
        ),
        el('td', {}, projectStatusBadge(p.status)),
        el('td', { class: 'col-center' }, `${p.versionCount}`, el('span', { class: 'cell-sub' }, ` / ${p.scheduleCount} sched.`)),
        el('td', { class: 'nowrap', title: `Created ${fmtDateOnly(p.createdAt)}` }, fmtRelative(p.updatedAt)),
        el('td', { class: 'col-right' }, toggle),
      ),
      detailRow,
    ];
  }

  async function load(page = state.page) {
    state.page = page;
    render(tbody, skeletonRows(cols, 5));
    try {
      const data = await fetchPage(state.page, pageSize);
      state.total = data.total;
      if (data.projects.length === 0) {
        render(tbody, fullRow(cols, emptyState({ title: 'No projects found', text: emptyText, compact: true })));
      } else {
        render(tbody, data.projects.flatMap(projectRows));
      }
      renderPagination(footer, { page: state.page, pageSize, total: state.total, noun: 'projects', onPage: (n) => void load(n) });
    } catch (err) {
      render(
        tbody,
        fullRow(cols, errorState({ title: 'Failed to load projects', text: err instanceof ApiError ? err.message : 'Check the API and try again.', onRetry: () => void load() })),
      );
      render(footer);
    }
  }

  const root = el(
    'div',
    { class: 'table-card' },
    el(
      'div',
      { class: 'table-scroll table-scroll--tall' },
      el(
        'table',
        { class: 'table' },
        el(
          'thead',
          {},
          el(
            'tr',
            {},
            el('th', {}, 'Project'),
            showOrg ? el('th', {}, 'Organisation') : null,
            el('th', {}, 'Owner'),
            el('th', {}, 'Status'),
            el('th', { class: 'col-center', title: 'AI-generated versions (root runs) / schedules. What-if runs are not counted.' }, 'Versions'),
            el('th', {}, 'Last update'),
            el('th', { class: 'col-right' }, ''),
          ),
        ),
        tbody,
      ),
    ),
    footer,
  );

  void load(1);
  return { root, reload: load };
}
